import { Effect, Option, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { defineMessageUnion } from "foldkit/message";
import { evo } from "foldkit/struct";
import type * as Update from "foldkit/update";

import { apiData, settle, start } from "../api/asyncData";
import { getAgentInbox } from "../api/commands/agents";
import { listFolders } from "../api/commands/notes";
import { InboxResponse } from "../api/schemas/agents";
import { FolderCounts } from "../api/schemas/notes";
import type { Api, Shared } from "../pages/shared";

/**
 * The shell's left sidebar (U16): its collapsed state (remembered per
 * browser), the Knowledge folder tree's disclosure, and the two root fetches it shows (folder counts, waiting agent
 * items). The fetches run only while `a2a-context` is on; the root decides
 * when (`refresh`). Tasks, notes and the inbox are not time-versioned, so
 * they always show live data, as the pages do (KTD8).
 */

/** Routes whose page carries its own search field; the shell's Search button focuses it instead of opening Records.
 * Home is not one: its feed has no search field, so the shell opens Records' search. */
const OWN_SEARCH: ReadonlySet<string> = new Set([
	"Records",
	"Tasks",
	"Knowledge",
	"Admin",
]);
export const hasOwnSearch = (routeTag: string): boolean =>
	OWN_SEARCH.has(routeTag);

const COLLAPSED_KEY = "sidebar-collapsed";

export const Model = S.Struct({
	collapsed: S.Boolean,
	/** The phone drawer; controlled so a navigation closes it. */
	drawerOpen: S.Boolean,
	/** The Knowledge folder tree, inline under Knowledge; closed by default (the Knowledge page has its own). */
	foldersOpen: S.Boolean,
	folderToggled: S.Array(S.String),
	folders: apiData(FolderCounts).schema,
	inbox: apiData(InboxResponse).schema,
});
export type Model = typeof Model.Type;

export const init = (collapsed = false): Model => ({
	collapsed,
	drawerOpen: false,
	foldersOpen: false,
	folderToggled: [],
	folders: AsyncData.Idle(),
	inbox: AsyncData.Idle(),
});

/** Storage can throw (private mode, blocked site data); the sidebar then starts expanded. */
export const readCollapsed = (): boolean => {
	try {
		return localStorage.getItem(COLLAPSED_KEY) === "true";
	} catch {
		return false;
	}
};

const writeCollapsed = (collapsed: boolean): void => {
	try {
		localStorage.setItem(COLLAPSED_KEY, String(collapsed));
	} catch {
		// Remembering is a convenience only.
	}
};

export const Message = defineMessageUnion({
	ClickedToggleSidebar: {},
	ClickedToggleFolders: {},
	ClickedToggleFolder: { path: S.String },
	CompletedFetchFolders: {
		generation: S.Number,
		result: listFolders.Result,
	},
	CompletedFetchInbox: {
		generation: S.Number,
		result: getAgentInbox.Result,
	},
	CompletedPersistSidebar: {},
});
export type Message = typeof Message.Type;

export const FetchFolders = Command.define("FetchNavFolders", {
	args: { generation: S.Number },
	messages: [Message.CompletedFetchFolders],
	execute: ({ generation }) =>
		listFolders
			.run({})
			.pipe(
				Effect.map((result) =>
					Message.CompletedFetchFolders({ generation, result }),
				),
			),
});

export const FetchInbox = Command.define("FetchNavInbox", {
	args: { generation: S.Number },
	messages: [Message.CompletedFetchInbox],
	execute: ({ generation }) =>
		getAgentInbox
			.run({})
			.pipe(
				Effect.map((result) =>
					Message.CompletedFetchInbox({ generation, result }),
				),
			),
});

export const PersistSidebar = Command.define("PersistSidebar", {
	args: { collapsed: S.Boolean },
	messages: [Message.CompletedPersistSidebar],
	execute: ({ collapsed }) =>
		Effect.sync(() => writeCollapsed(collapsed)).pipe(
			Effect.as(Message.CompletedPersistSidebar()),
		),
});

type Return = Update.Return<Model, Message, Api>;

/** Folder counts and the agents inbox, both live. */
export const refresh = (model: Model, generation: number): Return => ({
	model: evo(model, { folders: start, inbox: start }),
	commands: [FetchFolders({ generation }), FetchInbox({ generation })],
});

/** Signed out: drop the circle's data, keep the per-browser layout. */
export const reset = (model: Model): Model => init(model.collapsed);

/** Items waiting across every agent: mailbox items plus open tasks addressed to it. */
export const waitingCount = (model: Model): number =>
	Option.match(AsyncData.getData(model.inbox), {
		onNone: () => 0,
		onSome: ({ inbox }) => inbox.reduce((n, agent) => n + agent.pending, 0),
	});

export const update = (
	model: Model,
	message: Message,
	shared: Shared,
): Return =>
	Message.match<Return>(message, {
		ClickedToggleSidebar: () => {
			const collapsed = !model.collapsed;
			return {
				model: evo(model, { collapsed: () => collapsed }),
				commands: [PersistSidebar({ collapsed })],
			};
		},
		ClickedToggleFolders: () => ({
			model: evo(model, { foldersOpen: (open) => !open }),
		}),
		ClickedToggleFolder: ({ path }) => ({
			model: evo(model, {
				folderToggled: (toggled) =>
					toggled.includes(path)
						? toggled.filter((p) => p !== path)
						: [...toggled, path],
			}),
		}),
		CompletedFetchFolders: ({ generation, result }) => ({
			model:
				generation === shared.generation
					? evo(model, { folders: (folders) => settle(folders, result) })
					: model,
		}),
		CompletedFetchInbox: ({ generation, result }) => ({
			model:
				generation === shared.generation
					? evo(model, { inbox: (inbox) => settle(inbox, result) })
					: model,
		}),
		CompletedPersistSidebar: () => ({ model }),
	});
